import { Flame, Share2 } from "lucide-react";
import type { Archetype } from "./v3/archetype";
import "./WeekRecap.css";

/**
 * "Your Week in InSnip" recap: a shareable snapshot of the reader's week (cards read, streak, top
 * topics), plus the reader's earned archetype (v3): the identity is the second shareable, so
 * readers whose raw numbers are modest still have something worth posting. House style: no em
 * dashes.
 */
export default function WeekRecap({
  cardsThisWeek,
  streak,
  totalRead,
  saved,
  topics,
  archetype,
  onShare,
  onShareArchetype,
  onClose,
}: {
  cardsThisWeek: number;
  streak: number;
  totalRead: number;
  saved: number;
  topics: string[];
  archetype?: Archetype | null;
  onShare: () => void;
  onShareArchetype?: () => void;
  onClose: () => void;
}) {
  const minutes = Math.max(1, Math.round((cardsThisWeek * 25) / 60)); // ~25s per card
  return (
    <div className="recap-scrim" role="dialog" aria-modal="true" aria-label="Your week in InSnip" onClick={onClose}>
      <div className="recap-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="recap-close" onClick={onClose} aria-label="Close">&times;</button>
        <span className="recap-eyebrow">&#10024; Your week in InSnip</span>
        <h2 className="recap-title">{cardsThisWeek > 0 ? "Look at you go." : "Your week starts here."}</h2>

        <div className="recap-hero">
          <strong>{cardsThisWeek}</strong>
          <small>{cardsThisWeek === 1 ? "story this week" : "stories this week"}</small>
        </div>

        <div className="recap-stats">
          <div className="recap-stat">
            <strong className="recap-streak"><Flame size={16} aria-hidden="true" /> {streak}</strong>
            <small>day streak</small>
          </div>
          <div className="recap-stat">
            <strong>{minutes}m</strong>
            <small>well spent</small>
          </div>
          <div className="recap-stat">
            <strong>{saved}</strong>
            <small>saved</small>
          </div>
          <div className="recap-stat">
            <strong>{totalRead}</strong>
            <small>read all time</small>
          </div>
        </div>

        {topics.length ? (
          <div className="recap-topics">
            <span className="recap-topics-label">Your top topics</span>
            <div className="recap-chips">
              {topics.map((t) => <span key={t} className="recap-chip">{t}</span>)}
            </div>
          </div>
        ) : null}

        {archetype ? (
          <div className="recap-archetype">
            <span className="recap-arch-emblem" aria-hidden="true">{archetype.emblem}</span>
            <div className="recap-arch-copy">
              <span className="recap-topics-label">Your reader archetype</span>
              <strong className="recap-arch-name">{archetype.name}</strong>
              <small className="recap-arch-line">{archetype.line}</small>
              <em className="recap-arch-stat">{archetype.stat}</em>
            </div>
            {onShareArchetype ? (
              <button type="button" className="recap-arch-share" onClick={onShareArchetype}>
                <Share2 size={14} aria-hidden="true" /> Share it
              </button>
            ) : null}
          </div>
        ) : null}

        <p className="recap-foot">
          {cardsThisWeek > 0
            ? "Pure signal, no noise. See you tomorrow."
            : "Read a few snips and your week fills up. See you tomorrow."}
        </p>

        <div className="recap-actions">
          <button type="button" className="recap-share" onClick={onShare}><Share2 size={16} aria-hidden="true" /> Share my week</button>
          <button type="button" className="recap-done" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
